import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import style from '../style/components/CashbackWidget.module.scss';

/**
 * Presentational cashback balance / payout widget. Ported from the fragno SHOPWEB-694 web component
 * (rejected from the design-system library as app-specific). Pure props -> markup: the host supplies
 * `state` plus pre-formatted strings; the widget never fetches, formats currency, or computes dates.
 *
 * `state` selects one of nine presentations; any unrecognized value falls back to `default`. Surfaces
 * three lifecycle callbacks the host maps to analytics: `onViewed`, `onCtaClicked`, `onError`.
 */

const VALID_STATES = [
  'default',
  'available-pending',
  'payout-sent',
  'below-threshold',
  'zero',
  'first-time',
  'loading',
  'error',
  'payout-failed',
];

const PER_STATE_CTA_KEY = {
  'first-time': 'cashback.cta.firstTime',
  'payout-failed': 'cashback.cta.payoutFailed',
};

const SAFE_SCHEMES = ['http:', 'https:', 'mailto:', 'tel:'];

/* URL contract: '#' for empty/invalid/dangerous URLs (so a CTA without a wired destination doesn't
   scroll to top), relative paths pass through, only safe absolute schemes allowed — and never a
   protocol-relative //host, which inherits the page scheme and navigates off-origin. React does not
   block javascript: hrefs, so this guard is load-bearing even though managementHref is host-supplied. */
const sanitizeHref = (url) => {
  if (typeof url !== 'string') return '#';
  // Strip tab/newline/CR the way browsers do before navigating, so we evaluate the string the browser
  // will actually resolve — otherwise "/<TAB>/evil.com" slips past the protocol-relative check below
  // and the browser collapses it to "//evil.com" (off-origin).
  const trimmed = url.trim().replace(/[\t\n\r]/g, '');
  if (trimmed === '' || trimmed === '#') return '#';
  // Protocol-relative (//host, /\host, \\host) bypasses the scheme allowlist below — reject it first.
  if (/^[/\\]{2}/.test(trimmed)) return '#';
  if (/^(\/|\.\/|\.\.\/|#)/.test(trimmed)) return trimmed;
  if (!trimmed.includes(':')) return trimmed;
  return SAFE_SCHEMES.includes(`${trimmed.toLowerCase().split(':')[0]}:`)
    ? trimmed
    : '#';
};

/* Split a pre-formatted amount ("$87.00") into a large dollars part ("$87") and a small cents part
   (".00") for display sizing only. Not currency formatting: no rounding, locale, or separators. */
const splitAmount = (value) => {
  const str = value || '';
  const i = str.lastIndexOf('.');
  return i === -1
    ? { dollars: str, cents: '' }
    : { dollars: str.slice(0, i), cents: str.slice(i) };
};

const ArrowRightIcon = (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 8h10M9 4l4 4-4 4" />
  </svg>
);

const GiftIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="8" width="18" height="4" rx="1" />
    <path d="M12 8v13M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8M16.5 8a2.5 2.5 0 0 0 0-5C13 3 12 8 12 8" />
  </svg>
);

const WarningIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const InfoIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const XCircleIcon = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

const CashbackWidget = ({
  state: rawState,
  balance,
  pendingAmount,
  payoutDate,
  sentDate,
  thresholdAmount,
  managementHref,
  ctaLabel,
  ariaLabel,
  card,
  onViewed,
  onCtaClicked,
  onError,
}) => {
  const { t } = useTranslation();

  const state = VALID_STATES.includes(rawState) ? rawState : 'default';
  const href = sanitizeHref(managementHref);

  const show = {
    status: state === 'payout-sent',
    eyebrow:
      ['default', 'below-threshold', 'zero', 'payout-failed'].includes(state) &&
      !!balance,
    balance:
      [
        'default',
        'payout-sent',
        'below-threshold',
        'zero',
        'payout-failed',
      ].includes(state) && !!balance,
    split: state === 'available-pending',
    note: state === 'zero',
    banner: state === 'below-threshold' || state === 'payout-failed',
    empty: state === 'first-time',
    skeleton: state === 'loading',
    error: state === 'error',
  };
  const payoutDateText = state === 'payout-sent' ? sentDate : payoutDate;
  const showPayout =
    ['default', 'payout-sent', 'available-pending'].includes(state) &&
    !!payoutDateText;
  const showDivider = show.balance && showPayout;
  const { dollars, cents } = splitAmount(balance);
  const resolvedCtaLabel =
    ctaLabel ||
    (PER_STATE_CTA_KEY[state]
      ? t(PER_STATE_CTA_KEY[state])
      : t('cashback.cta.default'));

  /* Live-region text (WCAG 4.1.3). The visual error/banner nodes are aria-hidden; two always-present
     sr regions do the talking. Held in state and written in a post-commit effect so the regions are
     empty on first paint and populated after — without that deferral a mount straight into
     error/payout-failed/below-threshold would be silent (AT does not announce a region's initial
     content). The if/else keeps the regions mutually exclusive. */
  let polite = '';
  let assertive = '';
  if (state === 'error') {
    assertive = t('cashback.error');
  } else if (state === 'payout-failed') {
    assertive = t('cashback.srPayoutFailed');
  } else if (state === 'below-threshold') {
    polite = t('cashback.srBelowThreshold', { amount: thresholdAmount || '' });
  } else if (state === 'default' || state === 'payout-sent') {
    polite = balance ? t('cashback.srBalance', { balance }) : '';
  } else if (state === 'available-pending') {
    const parts = [];
    if (balance)
      parts.push(t('cashback.srAvailableAmount', { amount: balance }));
    if (pendingAmount)
      parts.push(t('cashback.srPendingAmount', { amount: pendingAmount }));
    if (parts.length)
      polite = parts.join(', ').replace(/^./, (c) => c.toUpperCase());
  }

  const [live, setLive] = useState({ polite: '', assertive: '' });
  useEffect(() => {
    setLive({ polite, assertive });
  }, [polite, assertive]);

  /* viewed: fire once after the first non-loading commit; a remount resets the ref and re-fires. */
  const viewedFiredRef = useRef(false);
  useEffect(() => {
    if (!viewedFiredRef.current && state !== 'loading') {
      viewedFiredRef.current = true;
      if (onViewed) onViewed({ state });
    }
  }, [state, onViewed]);

  /* error: fire on entering the error state (including an initial mount into it). */
  const prevStateRef = useRef(null);
  useEffect(() => {
    if (state === 'error' && prevStateRef.current !== 'error' && onError) {
      onError({ state: 'error', message: t('cashback.error') });
    }
    prevStateRef.current = state;
  }, [state, onError, t]);

  const handleCtaClick = (e) => {
    /* The CTA is visually non-interactive while loading (visibility:hidden + aria-hidden); guard the
       handler too so the "no navigation/callback during loading" contract holds even if a host
       stylesheet overrides the rule. */
    if (state === 'loading') {
      e.preventDefault();
      return;
    }
    if (onCtaClicked) onCtaClicked({ originalEvent: e, href, state });
    // Suppress navigation when the host cancels the event or there is no real destination.
    if (e.defaultPrevented || href === '#') e.preventDefault();
  };

  return (
    <div
      className={card ? `${style.container} ${style.card}` : style.container}
      data-state={state}
      data-testid="cashback-widget"
      role="group"
      aria-label={ariaLabel || t('cashback.title')}
    >
      {/* role="status"/"alert" are themselves live regions (implicit polite/assertive); the visible
          banner/error nodes below are aria-hidden so these regions are the sole announcers. */}
      <div className={style.srOnly} role="status">
        {live.polite}
      </div>
      <div className={style.srOnly} role="alert">
        {live.assertive}
      </div>

      <div className={style.header}>
        <span className={style.icon} aria-hidden="true">
          $
        </span>
        <span className={style.title}>{t('cashback.title')}</span>
      </div>

      {show.status && (
        <span className={style.status} data-testid="cashback-status">
          <span className={style.statusDot} aria-hidden="true" />
          {t('cashback.payoutSent')}
        </span>
      )}

      {show.eyebrow && (
        <p className={style.eyebrow}>{t('cashback.availableBalance')}</p>
      )}

      {show.balance && (
        <p className={style.balance} data-testid="cashback-balance">
          {/* aria-hidden visual dollars/cents + one sr-only span carrying the spoken value. "Available
              balance" is intentional for every balance-showing state including payout-sent, where the
              figure is the remaining available balance (the "Sent on" line dates a past payout). */}
          <span className={style.srOnly}>
            {t('cashback.srBalance', { balance })}
          </span>
          <span className={style.dollars} aria-hidden="true">
            {dollars}
          </span>
          <span className={style.cents} aria-hidden="true">
            {cents}
          </span>
        </p>
      )}

      {show.split && (
        <div className={style.split} data-testid="cashback-split">
          <div className={style.splitCol}>
            <span className={style.splitLabel}>{t('cashback.available')}</span>
            <span className={`${style.splitAmount} ${style.splitAvailable}`}>
              {balance || ''}
            </span>
          </div>
          <div className={style.splitCol}>
            <span className={style.splitLabel}>{t('cashback.pending')}</span>
            <span className={`${style.splitAmount} ${style.splitPending}`}>
              {pendingAmount || ''}
            </span>
          </div>
        </div>
      )}

      {show.note && <p className={style.note}>{t('cashback.zeroNote')}</p>}

      {showDivider && <hr className={style.divider} aria-hidden="true" />}

      {showPayout && (
        <p className={style.payoutLine}>
          <span className={style.payoutLabel}>
            {state === 'payout-sent'
              ? t('cashback.sentOn')
              : t('cashback.nextPayout')}
          </span>
          <span className={style.payoutDate}>{payoutDateText}</span>
        </p>
      )}

      {show.banner && (
        <div
          className={`${style.banner} ${
            state === 'payout-failed' ? style.bannerError : style.bannerInfo
          }`}
          data-testid="cashback-banner"
          aria-hidden="true"
        >
          <span className={style.bannerIcon} aria-hidden="true">
            {state === 'payout-failed' ? XCircleIcon : InfoIcon}
          </span>
          <p className={style.bannerText}>
            {state === 'payout-failed' ? (
              <Trans
                i18nKey="cashback.payoutFailed"
                components={{ strong: <strong /> }}
              />
            ) : (
              <Trans
                i18nKey="cashback.belowThreshold"
                values={{ amount: thresholdAmount || '' }}
                components={{ strong: <strong /> }}
              />
            )}
          </p>
        </div>
      )}

      {show.empty && (
        <div className={style.empty} data-testid="cashback-empty">
          <span className={style.emptyIcon} aria-hidden="true">
            {GiftIcon}
          </span>
          <p className={style.emptyHeading}>{t('cashback.firstTimeHeading')}</p>
          <p className={style.emptyCopy}>{t('cashback.firstTimeCopy')}</p>
        </div>
      )}

      {show.skeleton && (
        <div
          className={style.skeleton}
          aria-hidden="true"
          data-testid="cashback-skeleton"
        >
          <span className={`${style.skeletonLine} ${style.skeletonAmount}`} />
          <span className={`${style.skeletonLine} ${style.skeletonCents}`} />
          <hr className={style.skeletonDivider} />
          <span className={`${style.skeletonLine} ${style.skeletonDate}`} />
        </div>
      )}

      {show.error && (
        <div
          className={style.error}
          data-testid="cashback-error"
          aria-hidden="true"
        >
          <span className={style.errorIcon} aria-hidden="true">
            {WarningIcon}
          </span>
          <p className={style.errorText}>{t('cashback.error')}</p>
        </div>
      )}

      <a
        className={style.cta}
        href={href}
        onClick={handleCtaClick}
        data-testid="cashback-cta"
        {...(state === 'loading'
          ? { tabIndex: -1, 'aria-hidden': 'true' }
          : {})}
      >
        <span>{resolvedCtaLabel}</span>
        <span className={style.ctaArrow}>{ArrowRightIcon}</span>
      </a>
    </div>
  );
};

CashbackWidget.propTypes = {
  state: PropTypes.oneOf(VALID_STATES),
  balance: PropTypes.string,
  pendingAmount: PropTypes.string,
  payoutDate: PropTypes.string,
  sentDate: PropTypes.string,
  thresholdAmount: PropTypes.string,
  managementHref: PropTypes.string,
  ctaLabel: PropTypes.string,
  ariaLabel: PropTypes.string,
  card: PropTypes.bool,
  onViewed: PropTypes.func,
  onCtaClicked: PropTypes.func,
  onError: PropTypes.func,
};

CashbackWidget.defaultProps = {
  state: 'default',
  balance: undefined,
  pendingAmount: undefined,
  payoutDate: undefined,
  sentDate: undefined,
  thresholdAmount: undefined,
  managementHref: undefined,
  ctaLabel: undefined,
  ariaLabel: undefined,
  card: false,
  onViewed: undefined,
  onCtaClicked: undefined,
  onError: undefined,
};

export default CashbackWidget;
